import React, { useState } from 'react';
import type { Customer, Order } from '../types';
import PlusIcon from '../components/icons/PlusIcon';
import EditIcon from '../components/icons/EditIcon';
import TrashIcon from '../components/icons/TrashIcon';
import EyeIcon from '../components/icons/EyeIcon';
import CustomerModal from '../components/customers/CustomerModal';
import CustomerDetailModal from '../components/customers/CustomerDetailModal';

interface CustomerManagementPageProps {
  customers: Customer[];
  orders: Order[];
  onSave: (customer: Customer) => void;
  onDelete: (customerId: number) => void;
  currentStaffLabel?: string;
}

const CustomerManagementPage: React.FC<CustomerManagementPageProps> = ({
  customers,
  orders,
  onSave,
  onDelete,
  currentStaffLabel,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [viewingCustomer, setViewingCustomer] = useState<Customer | null>(null);

  const handleOpenModal = (customer: Customer | null = null) => {
    setEditingCustomer(customer);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCustomer(null);
  };

  const handleSave = (customer: Customer) => {
    onSave(customer);
    handleCloseModal();
  };

  const handleDelete = (customerId: number) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      onDelete(customerId);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <header className="bg-white shadow-md z-10 flex-shrink-0">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <h1 className="text-2xl font-bold text-gray-800" data-testid="customers-page-heading">Customer Management</h1>
            <button
              onClick={() => handleOpenModal()}
              className="bg-pink-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-pink-600 transition-colors duration-300 flex items-center"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Add New Customer
            </button>
          </div>
        </div>
      </header>
      <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
        <div className="bg-white shadow rounded-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Loyalty Points</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Orders</th>
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {customers.map((customer) => (
                <tr key={customer.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                      {customer.name}
                      {customer.studentVerified && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide bg-green-100 text-green-800 px-1.5 py-0.5 rounded">
                          Student
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{customer.phone}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{customer.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-bold text-pink-600">{customer.loyaltyPoints}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {orders.filter(o => o.customerId === customer.id).length} order{orders.filter(o => o.customerId === customer.id).length !== 1 ? 's' : ''}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button onClick={() => setViewingCustomer(customer)} className="text-blue-600 hover:text-blue-900 mr-4" title="View Details">
                      <EyeIcon className="w-5 h-5" />
                    </button>
                    <button onClick={() => handleOpenModal(customer)} className="text-pink-600 hover:text-pink-900 mr-4" title="Edit">
                      <EditIcon className="w-5 h-5" />
                    </button>
                    <button onClick={() => handleDelete(customer.id)} className="text-red-600 hover:text-red-900" title="Delete">
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
      {isModalOpen && (
        <CustomerModal
          customer={editingCustomer}
          onClose={handleCloseModal}
          onSave={handleSave}
          currentStaffLabel={currentStaffLabel}
        />
      )}
      {viewingCustomer && (
        <CustomerDetailModal
          customer={viewingCustomer}
          orders={orders}
          onClose={() => setViewingCustomer(null)}
        />
      )}
    </div>
  );
};

export default CustomerManagementPage;
